// TODO: Resolve issue with async thunks types
import {
  createSlice,
  createEntityAdapter,
  AsyncThunk,
  PayloadAction,
} from '@reduxjs/toolkit';

import { Study } from '@/shared/api/protocol-ts/model/dto_study_pb';
import {
  DefaultInitialState,
  LoadingStateType,
  SliceName,
} from '@/shared/config';

import { deleteStudy } from './studySlice.thunks';

const sortStudy = (a: Study, b: Study) => {
  const aUpdatedAt = a.Revision?.UpdatedAt?.toDate().getTime() ?? 0;
  const bUpdatedAt = b.Revision?.UpdatedAt?.toDate().getTime() ?? 0;

  return bUpdatedAt - aUpdatedAt;
};

export const studyAdapter = createEntityAdapter<Study>({
  selectId: (study) => study.ID,
  sortComparer: sortStudy,
});

type StudySliceInitialState = DefaultInitialState & {
  isDeleteStudyPending: boolean;
};

const initialStateFlags = {
  isDeleteStudyPending: false,
};

// TODO: Need to discuss this with the team
type StateFlags = keyof typeof initialStateFlags;

const initialState: StudySliceInitialState = {
  loading: 'idle',
  ...initialStateFlags,
};

const studyState = studyAdapter.getInitialState(initialState);

type StudyState = typeof studyState;

const studySlice = createSlice({
  name: SliceName.study,
  initialState: studyAdapter.getInitialState(initialState),
  reducers: {
    addOne: (state, action: PayloadAction<Study>) => {
      studyAdapter.addOne(state as StudyState, action.payload);
    },
    addMany: (state, action: PayloadAction<Study[]>) => {
      studyAdapter.addMany(state as StudyState, action.payload);
    },
    setMany: (state, action: PayloadAction<Study[]>) => {
      studyAdapter.setMany(state as StudyState, action.payload);
    },
    setNewestOne: (state, action: PayloadAction<Study>) => {
      const currentRevisionNumber =
        state.entities[action.payload.ID]?.Revision?.Number ?? 0;
      const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

      if (payloadRevisionNumber > currentRevisionNumber) {
        studyAdapter.setOne(state as StudyState, action.payload);
      }
    },
    setNewestMany: (state, action: PayloadAction<Study[]>) => {
      const items = action.payload.filter((item) => {
        const currentRevisionNumber =
          state.entities[item.ID]?.Revision?.Number ?? 0;
        const payloadRevisionNumber = item.Revision?.Number ?? 0;

        return payloadRevisionNumber > currentRevisionNumber;
      });

      studyAdapter.setMany(state as StudyState, items);
    },
    removeOne: (state, action: PayloadAction<string>) => {
      studyAdapter.removeOne(state as StudyState, action.payload);
    },
    removeAll: (state) => {
      studyAdapter.removeAll(state as StudyState);
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    const asyncRequestCreator = <A, B, C>(
      // @ts-expect-error WARN: C doesn't satisfy AsyncThunkConfig, but this is not public type. Just ignore this or remove this function and use builder itself.
      thunkAction: AsyncThunk<A, B, C>,
      pendingFlagKey: StateFlags,
      fullfiledCallback?: (payload: unknown) => void,
    ) => {
      builder.addCase(thunkAction.pending, (state) => {
        state[pendingFlagKey] = true;
      });
      builder.addCase(thunkAction.fulfilled, (state, { payload }) => {
        state[pendingFlagKey] = false;
        if (typeof fullfiledCallback === 'function') {
          fullfiledCallback(payload);
        }
      });
      builder.addCase(thunkAction.rejected, (state) => {
        state[pendingFlagKey] = false;
      });
    };

    asyncRequestCreator(deleteStudy, 'isDeleteStudyPending');
  },
});

export const { actions } = studySlice;

export default studySlice.reducer;
